// API cache utility for jobs data
// Cache duration: 5 minutes (300000 milliseconds)

const CACHE_KEY = 'credentia_jobs_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const API_URL = 'https://statusapi.credentia.com/api/get-jobs';

// Check if cache is valid (exists and not expired)
const isCacheValid = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return false;

    const { timestamp, data } = JSON.parse(cached);
    const now = Date.now();
    const age = now - timestamp;

    return age < CACHE_DURATION && data && Array.isArray(data);
  } catch (error) {
    console.error('Error reading cache:', error);
    return false;
  }
};

// Get cached data
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data } = JSON.parse(cached);
    return data;
  } catch (error) {
    console.error('Error reading cached data:', error);
    return null;
  }
};

// Set cache data
const setCachedData = (data) => {
  try {
    const cacheObject = {
      timestamp: Date.now(),
      data: data
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('Error setting cache:', error);
  }
};

// Clear cache
const clearCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

// Fetch jobs from API
const fetchJobsFromAPI = async () => {
  try {
    const response = await fetch(API_URL);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch jobs: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (result.success && Array.isArray(result.data)) {
      // Cache the raw API data
      setCachedData(result.data);
      return result.data;
    } else {
      throw new Error('Invalid API response format');
    }
  } catch (error) {
    console.error('Error fetching jobs from API:', error);
    throw error;
  }
};

// Get jobs (from cache or API)
// This ensures only one API call is made, even if multiple components call this function
let fetchPromise = null;

const getJobs = async () => {
  // Check cache first
  if (isCacheValid()) {
    // console.log('Using cached jobs data');
    return getCachedData();
  }

  // If there's already a fetch in progress, return that promise
  if (fetchPromise) {
    // console.log('API call already in progress, waiting...');
    return fetchPromise;
  }

  // Start new fetch
  // console.log('Fetching jobs from API...');
  fetchPromise = fetchJobsFromAPI();
  
  try {
    const data = await fetchPromise;
    return data;
  } finally {
    // Clear the promise so future calls can make new requests if needed
    fetchPromise = null;
  }
};

export { getJobs, clearCache, isCacheValid, API_URL };

